import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { OTSummaryByMonthItem } from "@/modules/hr/overtime/domain/types";
import { downloadCsvUtf8, escapeCsvCell } from "@/shared/lib/exportCsvUtf8";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Input, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Download, ExternalLink, HelpCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useOTSummaryByMonthQuery } from "../api/queries";
import { OTSummaryTour, useOTSummaryTour } from "../tour/OTSummaryTour";
import { OTSummaryTable } from "../ui/OTSummaryTable";

function sortSummary(
  rows: OTSummaryByMonthItem[],
  sortBy: string,
  sortOrder: "asc" | "desc",
): OTSummaryByMonthItem[] {
  const dir = sortOrder === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    let av: string | number | null = (a as Record<string, unknown>)[sortBy] as
      | string
      | number
      | null;
    let bv: string | number | null = (b as Record<string, unknown>)[sortBy] as
      | string
      | number
      | null;
    if (av == null) av = "";
    if (bv == null) bv = "";
    if (typeof av === "number" && typeof bv === "number")
      return dir * (av - bv);
    return dir * String(av).localeCompare(String(bv));
  });
}

function summaryToCsvRows(rows: OTSummaryByMonthItem[]): string[] {
  const header = ["ພະນັກງານ", "ອີເມວ", "ແຜ່ນການ", "ຕໍາແໜ່ງ", "ຊົ່ວໂມງ OT ລວມ"]
    .map(escapeCsvCell)
    .join(",");
  const dataRows = rows.map((row) =>
    [
      row.employeeName,
      row.employeeEmail,
      row.departmentName ?? "",
      row.positionTitle ?? "",
      row.totalHours.toFixed(1),
    ]
      .map(escapeCsvCell)
      .join(","),
  );
  return [header, ...dataRows];
}

export type OTSummarySearchParams = {
  year?: number;
  month?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
};

const MONTHS = [
  { value: "1", label: "ມັງກອນ" },
  { value: "2", label: "ກຸມພາ" },
  { value: "3", label: "ມີນາ" },
  { value: "4", label: "ເມສາ" },
  { value: "5", label: "ພຶດສະພາ" },
  { value: "6", label: "ມິຖຸນາ" },
  { value: "7", label: "ກໍລະກົດ" },
  { value: "8", label: "ສິງຫາ" },
  { value: "9", label: "ກັນຍາ" },
  { value: "10", label: "ຕຸລາ" },
  { value: "11", label: "ພະຈິກ" },
  { value: "12", label: "ທັນວາ" },
];

export function OTSummaryPage() {
  const nav = useNavigate({ from: "/app/hr/overtime/summary" });
  const search = useSearch({
    from: "/app/hr/overtime/summary",
  }) as OTSummarySearchParams;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const year = search.year ?? currentYear;
  const month = search.month ?? currentMonth;

  const [yearInput, setYearInput] = useState(String(year));
  const [monthInput, setMonthInput] = useState(String(month));

  useEffect(() => {
    setYearInput(String(year));
    setMonthInput(String(month));
  }, [year, month]);

  const sortBy = search.sort?.[0]?.field ?? "totalHours";
  const sortOrder = (search.sort?.[0]?.dir ?? "desc") as "asc" | "desc";

  const summaryQuery = useOTSummaryByMonthQuery(year, month);
  const rawData = summaryQuery.data?.data ?? [];
  const data = useMemo(
    () => sortSummary(rawData, sortBy, sortOrder),
    [rawData, sortBy, sortOrder],
  );

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          year,
          month,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
        },
      });
    },
    [nav, year, month],
  );

  const doNavPeriod = useCallback(
    (y: number, m: number) => {
      nav({ search: { year: y, month: m, sort: search.sort } });
    },
    [nav, search.sort],
  );

  const handleViewDetail = (row: OTSummaryByMonthItem) => {
    nav({
      to: "/app/hr/overtime/approvals",
      search: {
        offset: 0,
        limit: 20,
        sort: search.sort,
        periodYear: year,
        periodMonth: month,
        status: "approved",
        employeeId: row.employeeId,
        search: undefined,
      },
    });
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    setExporting(true);
    try {
      const csv = summaryToCsvRows(data).join("\r\n");
      downloadCsvUtf8(
        csv,
        `ot-summary-${year}-${String(month).padStart(2, "0")}.csv`,
      );
      toast.success("ສົ່ງອອກສຳເລັດ");
    } catch (err) {
      toast.error("ສົ່ງອອກບໍ່ສຳເລັດ", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setExporting(false);
    }
  }, [data, year, month]);

  const handleOpenPayroll = () => {
    const y = Number(yearInput) || year;
    const m = Number(monthInput) || month;
    nav({
      to: "/app/hr/payroll/period",
      search: {
        year: y,
        month: m,
        departmentId: undefined,
        status: undefined,
        search: undefined,
        sort: undefined,
      },
    });
  };

  const tour = useOTSummaryTour();

  return (
    <>
      <Header />
      <Main>
        <OTSummaryTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mb-6">
          <div className="mt-2 flex items-center gap-2">
            <h1 className="font-semibold text-2xl">ສະຫຼຸບ OT ຕາມເດືອນ</h1>
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
              title="ເລີ່ມທົດສອບການນຳທາງ"
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-5" />
            </Button>
          </div>
          <p className="text-muted-foreground text-sm">
            ສະຫຼຸບຂໍ້ມູນ OT ຕາມເດືອນຂອງພະນັກງານທີ່ອະນຸມັດ. ໃຊ້ສໍາລັບການຄິດເງິນເດືອນແລະຮັບເງິນ.
          </p>
        </div>

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="ot-summary-toolbar"
        >
          <div className="mb-4 flex flex-wrap items-end gap-3 px-2">
            <div className="min-w-[100px]">
              <span className="mb-1.5 block font-medium text-muted-foreground text-xs">
                ປີ (Year)
              </span>
              <Input
                type="number"
                min={2000}
                max={2100}
                value={yearInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setYearInput(v);
                  const y = Number(v) || currentYear;
                  doNavPeriod(y, Number(monthInput) || currentMonth);
                }}
                className="w-full"
              />
            </div>
            <div className="min-w-[140px]">
              <span className="mb-1.5 block font-medium text-muted-foreground text-xs">
                ເດືອນ (Month)
              </span>
              <SimpleSelect
                options={MONTHS}
                value={monthInput}
                onValueChange={(v) => {
                  setMonthInput(v);
                  const m = Number(v) || currentMonth;
                  doNavPeriod(Number(yearInput) || currentYear, m);
                }}
                placeholder="ເດືອນ"
                className="w-full"
              />
            </div>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={handleOpenPayroll}>
                <ExternalLink className="h-4 w-4" />
                ເປີດ Payroll ຊ່ວງເດືອນນີ້
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={exporting}
                onClick={handleExport}
              >
                <Download className="h-4 w-4" />
                {exporting ? "ກຳລັງສ້າງ..." : "ສົ່ງອອກ"}
              </Button>
            </div>
          </div>
        </div>

        <div className="mb-6" data-tourid="ot-summary-table">
          <OTSummaryTable
            data={data}
            isLoading={summaryQuery.isLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={handleSortingChange}
            onViewDetail={handleViewDetail}
          />
        </div>
      </Main>
    </>
  );
}
